import {
  ChevronDown,
  ChevronRight,
  Database,
  File,
  FileCode,
  FileSpreadsheet,
  Folder,
  FolderOpen,
  Image as ImageIcon,
  Landmark,
  Link2,
  StickyNote,
  Table2,
} from "lucide-react";
import {
  SourceNode,
  SourceFile,
  getFileExtension,
  normalizeSourceType,
} from "./sources-types";

const STROKE = 1.5;
const ICON_SIZE = 16;
export const SOURCE_LIST_ACCENT = "#8DE038";
export const getSourceRowPaddingLeft = (depth: number) => 12 + depth * 24;
export const SOURCE_ICON_BOX_SIZE = 28;

function FileTypeIcon({ node }: { node: SourceFile }) {
  const ext = getFileExtension(node).toLowerCase();
  const iconClass = "text-slate-600";
  if (["pdf"].includes(ext)) {
    return <File size={ICON_SIZE} strokeWidth={STROKE} className={iconClass} />;
  }
  if (["csv", "xlsx", "xls"].includes(ext)) {
    return (
      <FileSpreadsheet size={ICON_SIZE} strokeWidth={STROKE} className={iconClass} />
    );
  }
  if (["js", "ts", "tsx", "jsx", "py", "json", "html", "css"].includes(ext)) {
    return <FileCode size={ICON_SIZE} strokeWidth={STROKE} className={iconClass} />;
  }
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) {
    return <ImageIcon size={ICON_SIZE} strokeWidth={STROKE} className={iconClass} />;
  }
  return <File size={ICON_SIZE} strokeWidth={STROKE} className={iconClass} />;
}

function IconBox({
  children,
  className,
}: {
  children: React.ReactNode;
  className: string;
}) {
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-lg transition-colors ${className}`}
      style={{ width: SOURCE_ICON_BOX_SIZE, height: SOURCE_ICON_BOX_SIZE }}
    >
      {children}
    </div>
  );
}

export interface SourceNodeIconProps {
  node: SourceNode;
  expanded: boolean;
  isFolderNode: boolean;
}

export function SourceNodeIcon({
  node,
  expanded,
  isFolderNode,
}: SourceNodeIconProps) {
  if (isFolderNode) {
    const Icon = expanded ? FolderOpen : Folder;
    return (
      <div className="flex shrink-0 items-center gap-0.5">
        {expanded ? (
          <ChevronDown size={18} strokeWidth={STROKE} className="text-amber-700/90" />
        ) : (
          <ChevronRight size={18} strokeWidth={STROKE} className="text-amber-700/90" />
        )}
        <IconBox className="bg-amber-50">
          <Icon size={ICON_SIZE} strokeWidth={STROKE} className="text-amber-700" />
        </IconBox>
      </div>
    );
  }

  const fileNode = node as SourceFile;
  const type = normalizeSourceType(String(fileNode.type));

  if (type === "file") {
    return (
      <IconBox className="bg-slate-100">
        <FileTypeIcon node={fileNode} />
      </IconBox>
    );
  }

  if (type === "dataRepo") {
    return (
      <IconBox className="bg-emerald-50">
        <Database size={ICON_SIZE} strokeWidth={STROKE} className="text-emerald-600" />
      </IconBox>
    );
  }

  if (type === "dataBank") {
    return (
      <IconBox className="bg-teal-50">
        <Landmark size={ICON_SIZE} strokeWidth={STROKE} className="text-teal-700" />
      </IconBox>
    );
  }

  if (type === "table") {
    return (
      <IconBox className="bg-violet-50">
        <Table2 size={ICON_SIZE} strokeWidth={STROKE} className="text-violet-600" />
      </IconBox>
    );
  }

  if (type === "quickNote") {
    return (
      <IconBox className="bg-amber-50">
        <StickyNote size={ICON_SIZE} strokeWidth={STROKE} className="text-amber-700" />
      </IconBox>
    );
  }

  if (type === "link") {
    return (
      <IconBox className="bg-blue-50">
        <Link2 size={ICON_SIZE} strokeWidth={STROKE} className="text-blue-600" />
      </IconBox>
    );
  }

  return (
    <IconBox className="bg-slate-100">
      <FileTypeIcon node={fileNode} />
    </IconBox>
  );
}
